<template>
  <v-dialog :value="value" width="500px" persistent>
    <v-card>
      <v-card-title>Enter the code we emailed you</v-card-title>
      <v-card-text>
        <v-row class="mb-4">We sent a code to {{ this.email }}</v-row>
        <v-row class="mb-4">
          <v-col v-for="(digit, index) in code" :key="index" cols="2" class="text-center">
            <v-text-field
              v-model="code[index]"
              maxlength="1"
              outlined
              hide-details
              type="number"
              ref="digitFields"
              class="centered-input"
              :disabled="isLoading"
              @input="onInput(index)"
              @keydown.backspace="onBackspace(index, $event)"
              @paste.prevent="onPaste($event, index)"
            />
          </v-col>
        </v-row>
        <v-row class="mb-2">
          <span v-if="remainingSeconds > 0"> Expires in: {{ formattedRemainingTime }} </span>
          <span v-else> Code expired. Please request a new one. </span>
        </v-row>
        <v-row>Reference : {{ localReference }}</v-row>
      </v-card-text>
      <LoadingOverlay :value="isLoading" message="Please wait..." />
      <v-card-actions>
        <g-button label="Close" @onclick="close" />
        <div class="flex-grow-1"></div>
        <g-button label="Resend" :disabled="isLoading" @onclick="resend" />
        <g-button type="primary" label="Submit" :disabled="isLoading || !isAllCodeFilled" @onclick="submit" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { LoadingOverlay } from "@/modules/loading";

export default {
  components: {
    LoadingOverlay,
  },
  props: {
    value: Boolean,
    email: String,
    password: String,
    reference: String,
    expiresAt: String,
    loggedin: Function,
  },
  data() {
    return {
      isLoading: false,
      code: Array(6).fill(""),
      localReference: "",
      localExpiresAt: "",
      countdownTimer: null,
      remainingSeconds: 0,
    };
  },
  watch: {
    value(v) {
      if (!v) return;
      this.code = Array(6).fill("");
      this.localReference = this.reference;
      this.localExpiresAt = this.expiresAt;
      this.startCountdown();
    },
  },
  computed: {
    fullCode() {
      return this.code.join("");
    },
    isAllCodeFilled() {
      return this.code.every((d) => d !== "");
    },
    formattedRemainingTime() {
      const total = this.remainingSeconds;
      const minutes = Math.floor(total / 60);
      const seconds = total % 60;
      return String(minutes).padStart(2, "0") + ":" + String(seconds).padStart(2, "0");
    },
  },
  methods: {
    startCountdown() {
      if (!this.localExpiresAt) return;

      this.clearCountdown();
      this.updateRemainingTime();

      this.countdownTimer = setInterval(() => this.updateRemainingTime(), 1000);
    },
    clearCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
    updateRemainingTime() {
      const expires = new Date(this.localExpiresAt).getTime();
      const now = Date.now();
      const diffSeconds = Math.floor((expires - now) / 1000);

      this.remainingSeconds = diffSeconds > 0 ? diffSeconds : 0;

      if (diffSeconds <= 0) {
        this.clearCountdown();
      }
    },
    onInput(index) {
      const value = this.code[index];
      // Only digits allowed
      if (!/^[0-9]$/.test(value)) {
        this.code[index] = "";
        return;
      }
      // Move to next input if this one is filled
      if (index < this.code.length - 1) {
        this.$refs.digitFields[index + 1].focus();
      } else {
        this.submit();
      }
    },
    onBackspace(index, event) {
      if (!this.code[index] && index > 0) {
        this.$refs.digitFields[index - 1].focus();
      }
    },
    close() {
      this.$emit("input", false);
    },
    resend() {
      this.isLoading = true;
      this.code = Array(6).fill("");
      this.$store
        .dispatch("resendOtp", this.localReference)
        .then((res) => {
          this.localReference = res.reference;
          this.localExpiresAt = res.expires_at;
          this.startCountdown();
          this.$root.$success("A new code has been sent to your email");
        })
        .catch((err) => {
          this.$root.$error(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    submit() {
      this.isLoading = true;
      const creds = { email: this.email, password: this.password };
      const otp = { reference: this.localReference, code: this.fullCode };
      this.$store
        .dispatch("verifyOtp", { creds, otp })
        .then((account) => {
          this.$root.$success("Login successful");
          this.$router.push({ name: "home" });
          this.loggedin(account);
        })
        .catch((err) => {
          this.$root.$error(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onPaste(event, startIndex) {
      const clipboardData = event.clipboardData || window.clipboardData;
      const pastedText = clipboardData ? clipboardData.getData("text") : "";
      if (!pastedText) return;

      const digits = pastedText.replace(/\D/g, "").split(""); // Keep only digits
      if (!digits.length) return;

      let i = 0;
      while (i < digits.length && startIndex + i < this.code.length) {
        this.$set(this.code, startIndex + i, digits[i]);
        i++;
      }

      this.$nextTick(() => {
        if (this.isAllCodeFilled) {
          this.submit();
          return;
        }

        const nextEmptyIndex = this.code.findIndex((d) => d === "");
        if (nextEmptyIndex !== -1 && this.$refs.digitFields[nextEmptyIndex]) {
          this.$refs.digitFields[nextEmptyIndex].focus();
        }
      });
    },
  },
  beforeDestroy() {
    this.clearCountdown();
  },
};
</script>

<style lang="scss">
.centered-input input {
  text-align: center;
}
</style>
